import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Clock, Save, Loader2, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../lib/auth-context';

const DAYS = [1, 2, 3, 4, 5, 6, 0]; // Mon-Sat, Sun
const DAY_LABELS = { 0: 'Sunday', 1: 'Monday', 2: 'Tuesday', 3: 'Wednesday', 4: 'Thursday', 5: 'Friday', 6: 'Saturday' };

export default function AvailabilityPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'superadmin' || user?.role === 'admin';
  const isInstructor = user?.role === 'instructor';

  const [instructors, setInstructors] = useState([]);
  const [selectedInstructor, setSelectedInstructor] = useState('');
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // For admin: load instructor list (instructors + superadmin only — regular admins can't be hosts).
  useEffect(() => {
    if (isAdmin) {
      Promise.all([
        api.get('/api/users', { params: { role: 'instructor', limit: 50 } }),
        api.get('/api/users', { params: { role: 'admin', limit: 20 } }),
      ]).then(([r1, r2]) => {
        const superadmins = (r2.data.users || []).filter(u => u.role === 'superadmin');
        const all = [...(r1.data.users || []), ...superadmins];
        const unique = all.filter((u, i, a) => a.findIndex(x => x.id === u.id) === i);
        setInstructors(unique);
      });
    } else if (isInstructor && (user?.id || user?.user_id)) {
      setSelectedInstructor(user.id || user.user_id);
    }
  }, [user]);

  // Load slots for selected instructor
  useEffect(() => {
    if (!selectedInstructor) return;
    setLoading(true);
    api.get(`/api/availability/${selectedInstructor}`)
      .then(res => setSlots(res.data.slots || []))
      .finally(() => setLoading(false));
  }, [selectedInstructor]);

  const addSlot = (day) => {
    setSlots(prev => [...prev, { instructor_id: selectedInstructor, day_of_week: day, start_time: '09:00', end_time: '17:00' }]);
  };

  const updateSlot = (idx, field, value) => {
    setSlots(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };

  const removeSlot = (idx) => {
    setSlots(prev => prev.filter((_, i) => i !== idx));
  };

  const toggleDay = (day) => {
    const daySlots = slots.filter(s => s.day_of_week === day);
    if (daySlots.length > 0) {
      // Remove all slots for this day
      setSlots(prev => prev.filter(s => s.day_of_week !== day));
    } else {
      // Add default slot
      addSlot(day);
    }
  };

  const saveAvailability = async () => {
    setSaving(true);
    try {
      await api.post('/api/availability', {
        instructor_id: selectedInstructor,
        slots: slots.map(s => ({ ...s, instructor_id: selectedInstructor }))
      });
      toast.success('Availability saved');
    } catch (err) {
      toast.error('Failed to save');
    }
    setSaving(false);
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="availability-page">
      <div>
        <h1 className="font-heading text-3xl font-semibold tracking-tight">
          {isInstructor ? 'My Availability' : 'Instructor Availability'}
        </h1>
        <p className="text-muted-foreground mt-1">
          {isInstructor ? 'Set your available hours for sessions' : 'Manage instructor availability for scheduling'}
        </p>
      </div>

      {/* Admin: instructor selector */}
      {isAdmin && (
        <Card className="rounded-2xl">
          <CardContent className="pt-5">
            <label className="text-sm font-medium mb-1.5 block">Select Instructor</label>
            <Select value={selectedInstructor} onValueChange={setSelectedInstructor}>
              <SelectTrigger className="max-w-sm" data-testid="availability-instructor-select">
                <SelectValue placeholder="Choose an instructor" />
              </SelectTrigger>
              <SelectContent>
                {instructors.map(i => <SelectItem key={i.id} value={i.id}>{i.name} ({i.role})</SelectItem>)}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      )}

      {/* Availability slots */}
      {(selectedInstructor || isInstructor) && (
        <Card className="rounded-2xl">
          <CardContent className="pt-5">
            {loading ? (
              <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
            ) : (
              <div className="space-y-3">
                {DAYS.map(day => {
                  const daySlots = slots.filter(s => s.day_of_week === day);
                  const isAvailable = daySlots.length > 0;
                  return (
                    <div key={day} className={`rounded-xl border p-4 transition-colors ${isAvailable ? 'bg-primary/5 border-primary/20' : 'bg-muted/20'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <Switch
                            checked={isAvailable}
                            onCheckedChange={() => toggleDay(day)}
                            data-testid={`day-toggle-${day}`}
                          />
                          <h3 className="text-sm font-medium">{DAY_LABELS[day]}</h3>
                          {!isAvailable && <span className="text-xs text-muted-foreground">Unavailable</span>}
                        </div>
                        {isAvailable && (
                          <Button variant="outline" size="sm" onClick={() => addSlot(day)} className="h-7 text-xs">
                            <Plus className="h-3 w-3 mr-1" />Add Slot
                          </Button>
                        )}
                      </div>
                      {isAvailable && (
                        <div className="space-y-2 ml-12">
                          {daySlots.map((slot) => {
                            const globalIdx = slots.indexOf(slot);
                            return (
                              <div key={globalIdx} className="flex items-center gap-2">
                                <Input type="time" value={slot.start_time} onChange={e => updateSlot(globalIdx, 'start_time', e.target.value)} className="w-28 h-8 text-sm" />
                                <span className="text-xs text-muted-foreground">to</span>
                                <Input type="time" value={slot.end_time} onChange={e => updateSlot(globalIdx, 'end_time', e.target.value)} className="w-28 h-8 text-sm" />
                                {daySlots.length > 1 && (
                                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => removeSlot(globalIdx)}>
                                    <X className="h-3.5 w-3.5" />
                                  </Button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}

                <Button onClick={saveAvailability} disabled={saving} className="w-full mt-4" data-testid="save-availability-button">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  Save Availability
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
